import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabase';
import theme from '../theme';

function BadgeVerificato({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 4, flexShrink: 0 }}>
      <path d="M8 1L2 3.5V8c0 3.3 2.5 5.8 6 7 3.5-1.2 6-3.7 6-7V3.5L8 1z" fill={theme.colors.primary}/>
      <path d="M5.5 8l2 2 3-3" stroke={theme.colors.white} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IdentityVerification({ session, profile, onProfileUpdate }) {
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docStep, setDocStep] = useState(null);
  const [fronteUrl, setFronteUrl] = useState(null);
  const [identityStatus, setIdentityStatus] = useState(profile?.identity_status || 'non_verificato');
  const docInputRef = useRef(null);
  const retroInputRef = useRef(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    setIdentityStatus(profile?.identity_status || 'non_verificato');
  }, [profile]);

  useEffect(() => {
    if (identityStatus !== 'in_verifica') {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }

    pollingRef.current = setInterval(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('identity_status')
        .eq('id', session.user.id)
        .single();

      if (data && data.identity_status !== 'in_verifica') {
        setIdentityStatus(data.identity_status);
        clearInterval(pollingRef.current);
        await onProfileUpdate(session.user.id);
      }
    }, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [identityStatus]);

  const handleDocClick = (step) => {
    if (step === 'fronte' && docInputRef.current) {
      docInputRef.current.value = '';
      docInputRef.current.click();
    }
    if (step === 'retro' && retroInputRef.current) {
      retroInputRef.current.value = '';
      retroInputRef.current.click();
    }
  };

  const handleFronteUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('Il documento è troppo grande. Massimo 10MB.'); return; }
    setUploadingDoc(true);
    const ext = file.name.split('.').pop();
    const fileName = `${session.user.id}_fronte.${ext}`;
    const { error } = await supabase.storage.from('identity-docs').upload(fileName, file, { upsert: true });
    if (error) { alert('Errore caricamento fronte: ' + error.message); setUploadingDoc(false); return; }
    setFronteUrl(fileName);
    setDocStep('retro');
    setUploadingDoc(false);
  };

  const handleRetroUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('Il documento è troppo grande. Massimo 10MB.'); return; }
    setUploadingDoc(true);
    const ext = file.name.split('.').pop();
    const fileName = `${session.user.id}_retro.${ext}`;
    const { error } = await supabase.storage.from('identity-docs').upload(fileName, file, { upsert: true });
    if (error) { alert('Errore caricamento retro: ' + error.message); setUploadingDoc(false); return; }
    const { error: updateError } = await supabase.from('profiles').update({
      identity_status: 'in_verifica',
      identity_document_url: `${fronteUrl}|${fileName}`,
    }).eq('id', session.user.id);
    if (updateError) { alert('Errore aggiornamento profilo: ' + updateError.message); setUploadingDoc(false); return; }
    setIdentityStatus('in_verifica');
    setDocStep(null);
    setFronteUrl(null);
    await onProfileUpdate(session.user.id);
    setUploadingDoc(false);
  };

  if (identityStatus === 'verificato') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, background: theme.colors.primaryLight, borderRadius: theme.radius.md, padding: '12px 16px' }}>
        <BadgeVerificato size={20} />
        <div>
          <div style={{ fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.primary }}>Identità verificata</div>
          <div style={{ fontSize: theme.font.size.sm, color: '#555' }}>Il tuo documento è stato verificato con successo.</div>
        </div>
      </div>
    );
  }

  if (identityStatus === 'in_verifica') {
    return (
      <div style={{ background: theme.colors.warningBgSoft, borderRadius: theme.radius.md, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>⏳</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.warningText }}>Verifica in corso</div>
          <div style={{ fontSize: theme.font.size.sm, color: '#555' }}>Il tuo documento è in fase di revisione. Ti avviseremo a breve.</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, marginBottom: 10, lineHeight: 1.5 }}>
        Carica fronte e retro di un documento d'identità valido per ottenere il badge verificato. Il documento è privato e visibile solo agli amministratori.
      </div>

      {docStep === null && (
        <button onClick={() => setDocStep('fronte')} style={{
          width: '100%', background: theme.colors.primary, color: theme.colors.white,
          border: 'none', borderRadius: theme.radius.md, padding: '12px',
          fontSize: theme.font.size.md, fontWeight: 600, cursor: 'pointer'
        }}>
          🪪 Inizia verifica identità
        </button>
      )}

      {docStep === 'fronte' && (
        <div>
          <div style={{ background: theme.colors.infoBg, borderRadius: theme.radius.md, padding: '10px 14px', marginBottom: 10, fontSize: theme.font.size.base, color: theme.colors.info, fontWeight: 600 }}>
            📸 Passo 1 di 2 — Carica il <strong>fronte</strong> del documento
          </div>
          <button onClick={() => handleDocClick('fronte')} disabled={uploadingDoc} style={{
            width: '100%', background: uploadingDoc ? theme.colors.textMuted : theme.colors.info, color: theme.colors.white,
            border: 'none', borderRadius: theme.radius.md, padding: '12px', fontSize: theme.font.size.md,
            fontWeight: 600, cursor: uploadingDoc ? 'not-allowed' : 'pointer'
          }}>
            {uploadingDoc ? 'Caricamento...' : '📷 Carica fronte documento'}
          </button>
          <button onClick={() => setDocStep(null)} style={{
            width: '100%', background: 'none', border: 'none',
            color: theme.colors.textMuted, fontSize: theme.font.size.base, marginTop: 8, cursor: 'pointer'
          }}>Annulla</button>
          <input ref={docInputRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFronteUpload} />
        </div>
      )}

      {docStep === 'retro' && (
        <div>
          <div style={{ background: theme.colors.primaryLight, borderRadius: theme.radius.md, padding: '10px 14px', marginBottom: 10, fontSize: theme.font.size.base, color: theme.colors.primary, fontWeight: 600 }}>
            ✅ Fronte caricato con successo!
          </div>
          <div style={{ background: theme.colors.infoBg, borderRadius: theme.radius.md, padding: '10px 14px', marginBottom: 10, fontSize: theme.font.size.base, color: theme.colors.info, fontWeight: 600 }}>
            📸 Passo 2 di 2 — Carica il <strong>retro</strong> del documento
          </div>
          <button onClick={() => handleDocClick('retro')} disabled={uploadingDoc} style={{
            width: '100%', background: uploadingDoc ? theme.colors.textMuted : theme.colors.info, color: theme.colors.white,
            border: 'none', borderRadius: theme.radius.md, padding: '12px', fontSize: theme.font.size.md,
            fontWeight: 600, cursor: uploadingDoc ? 'not-allowed' : 'pointer'
          }}>
            {uploadingDoc ? 'Caricamento...' : '📷 Carica retro documento'}
          </button>
          <input ref={retroInputRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleRetroUpload} />
        </div>
      )}

      <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginTop: 8, textAlign: 'center' }}>
        Formati accettati: JPG, PNG, PDF — Max 10MB per foto
      </div>
    </div>
  );
}

export default IdentityVerification;